import {
  applyAction,
  createMatch,
  createPlayerObservation,
  getLegalActions,
  getPlayerObservationHash,
  getStateHash,
  getStateInvariantViolations,
  type ActionValidationFailure,
  type EngineContext,
  type GameAction,
  type GameEvent,
  type CurrentCoreAlphaGameState,
  type MatchSetupFailure,
  type MatchSetupInput,
  type PlayerActionSource,
  PlayerActionSourceFailure,
  type PlayerDecisionContext,
  type PlayerObservationV1,
  type ReplayDefinitionV3,
  type ReplayDefinitionV4,
  type ReplayDefinitionV5,
} from "@fate-kingdom/engine";
import { canonicalStringify } from "@fate-kingdom/shared";

const DEFAULT_MAXIMUM_ACTIONS = 10_000;
const DEFAULT_MAXIMUM_TURNS = 500;

export type HeadlessInvariantCheck = "every-step" | "final-only";
/** Full is audit-grade; compact omits per-step hashes for deterministic seed discovery only. */
export type HeadlessTraceMode = "full" | "compact";

export interface HeadlessMatchRunnerOptions {
  /** Inclusive action count cap; default is 10,000 successful actions. */
  readonly maximumActions?: number;
  /** A match may finish within this numbered turn; default is 500. */
  readonly maximumTurns?: number;
  readonly invariantCheck?: HeadlessInvariantCheck;
  readonly traceMode?: HeadlessTraceMode;
  /** Defaults to the engine-owned projection; injectable only for boundary tests. */
  readonly playerObservationProjector?: PlayerObservationProjector;
}

export type PlayerObservationProjector = (
  state: Readonly<CurrentCoreAlphaGameState>,
  perspectivePlayerId: "player-1" | "player-2",
) => PlayerObservationV1;

export type PlayerActionSources = Readonly<Record<"player-1" | "player-2", PlayerActionSource>>;

export interface HeadlessTraceEntry {
  readonly step: number;
  readonly action: GameAction;
  readonly stateHashBefore: string | null;
  readonly stateHashAfter: string | null;
  readonly events: readonly GameEvent[];
}

export interface HeadlessActionSourceContext {
  readonly step: number;
  readonly state: CurrentCoreAlphaGameState;
  readonly legalActions: readonly GameAction[];
}

/**
 * An injected source owns all choice. The runner only verifies and applies its
 * serializable action; it intentionally contains no bot or selection policy.
 */
export interface HeadlessActionSource {
  nextAction(context: HeadlessActionSourceContext): GameAction | null;
}

export interface HeadlessRunnerFailure {
  readonly code:
    | "ACTION_NOT_IN_LEGAL_SET"
    | "ACTION_SOURCE_ERROR"
    | "EMPTY_LEGAL_ACTION_SET"
    | "OBSERVATION_PROJECTION_ERROR"
    | "PLAYER_SOURCE_ID_MISMATCH"
    | "WRONG_PLAYER_CONTEXT"
    | "ENGINE_APPLICATION_ERROR"
    | "INVARIANT_VIOLATION";
  readonly message: string;
  readonly details: Readonly<Record<string, string | number | boolean | null>>;
}

export interface HeadlessStepApplied {
  readonly ok: true;
  readonly entry: HeadlessTraceEntry;
}

export interface HeadlessStepRejected {
  readonly ok: false;
  readonly status: "illegal-action" | "engine-failure" | "invariant-failure";
  readonly failure: HeadlessRunnerFailure | ActionValidationFailure;
}

export type HeadlessStepResult = HeadlessStepApplied | HeadlessStepRejected;

interface HeadlessRunSnapshot {
  readonly actionCount: number;
  readonly turnNumber: number;
  readonly finalStateHash: string;
  readonly state: CurrentCoreAlphaGameState;
  readonly trace: readonly HeadlessTraceEntry[];
}

export type HeadlessRunResult =
  | (HeadlessRunSnapshot & {
      readonly status: "finished";
      readonly winnerPlayerId: "player-1" | "player-2";
      readonly reason: "two-victory-banners" | "no-territory";
    })
  | (HeadlessRunSnapshot & {
      readonly status: "action-limit-reached" | "turn-limit-reached" | "action-source-ended";
    })
  | (HeadlessRunSnapshot & {
      readonly status: "illegal-action" | "engine-failure" | "invariant-failure";
      readonly failure: HeadlessRunnerFailure | ActionValidationFailure;
    });

export type HeadlessRunnerCreationResult =
  | { readonly ok: true; readonly runner: HeadlessMatchRunner }
  | {
      readonly ok: false;
      readonly failure:
        | MatchSetupFailure
        | {
            readonly code: "UNSUPPORTED_GAME_STATE_SCHEMA" | "INVARIANT_VIOLATION";
            readonly message: string;
            readonly path: string | null;
            readonly details: Readonly<Record<string, string | number | boolean | null>>;
          };
    };

export interface HeadlessMatchRunner {
  readonly state: CurrentCoreAlphaGameState;
  readonly trace: readonly HeadlessTraceEntry[];
  getLegalActions(): readonly GameAction[];
  apply(action: GameAction): HeadlessStepResult;
  isFinished(): boolean;
  /** Replays retain the schema associated with their deterministically created match. */
  exportReplay(): ReplayDefinitionV3 | ReplayDefinitionV4 | ReplayDefinitionV5;
  run(source: HeadlessActionSource): HeadlessRunResult;
  runPlayerSources(sources: PlayerActionSources): HeadlessRunResult;
}

interface NormalizedOptions {
  readonly maximumActions: number;
  readonly maximumTurns: number;
  readonly invariantCheck: HeadlessInvariantCheck;
  readonly traceMode: HeadlessTraceMode;
  readonly playerObservationProjector: PlayerObservationProjector;
}

function cloneSerializable<T>(value: T): T {
  // Engine state, actions, events, traces, and replays are serializable by
  // contract. Cloning need not sort keys; canonical serialization remains
  // reserved for structural action membership and deterministic hashes.
  return JSON.parse(JSON.stringify(value)) as T;
}

function actionKey(action: GameAction): string {
  return canonicalStringify(action);
}

/**
 * Structural membership check shared by runners and interactive adapters.
 * The engine remains the final action validator; this prevents an adapter from
 * constructing or silently repairing an action outside its supplied legal set.
 */
export function isActionInLegalSet(
  action: GameAction,
  legalActions: readonly GameAction[],
): boolean {
  const key = actionKey(action);
  return legalActions.some((candidate) => actionKey(candidate) === key);
}

/**
 * Builds the only context a player-controlled action source may receive.
 * It intentionally projects the engine state before handing it to a bot.
 */
export function createSafePlayerDecisionContext(
  state: Readonly<CurrentCoreAlphaGameState>,
  playerId: "player-1" | "player-2",
  step: number,
  playerObservationProjector: PlayerObservationProjector = createPlayerObservation,
): PlayerDecisionContext {
  if (state.activePlayerId !== playerId) {
    throw new PlayerActionSourceFailure(
      "WRONG_PLAYER_CONTEXT",
      "A player decision context may only be created for the active player.",
      { activePlayerId: state.activePlayerId, requestedPlayerId: playerId },
    );
  }

  const observation = playerObservationProjector(state, playerId);
  return cloneSerializable({
    step,
    playerId,
    observation,
    observationHash: getPlayerObservationHash(observation),
    legalActions: getLegalActions(state, playerId),
  });
}

function normalizePositiveInteger(
  value: number | undefined,
  fallback: number,
  name: string,
): number {
  const normalized = value ?? fallback;
  if (!Number.isSafeInteger(normalized) || normalized < 1) {
    throw new RangeError(`${name} must be a positive safe integer.`);
  }
  return normalized;
}

function normalizeOptions(options: HeadlessMatchRunnerOptions | undefined): NormalizedOptions {
  return {
    maximumActions: normalizePositiveInteger(
      options?.maximumActions,
      DEFAULT_MAXIMUM_ACTIONS,
      "maximumActions",
    ),
    maximumTurns: normalizePositiveInteger(
      options?.maximumTurns,
      DEFAULT_MAXIMUM_TURNS,
      "maximumTurns",
    ),
    invariantCheck: options?.invariantCheck ?? "every-step",
    traceMode: options?.traceMode ?? "full",
    playerObservationProjector: options?.playerObservationProjector ?? createPlayerObservation,
  };
}

function invariantFailure(
  state: Readonly<CurrentCoreAlphaGameState>,
): HeadlessRunnerFailure | null {
  const violation = getStateInvariantViolations(state)[0];
  if (violation === undefined) return null;
  return {
    code: "INVARIANT_VIOLATION",
    message: violation.message,
    details: { invariantCode: violation.code, path: violation.path },
  };
}

function snapshot(
  state: Readonly<CurrentCoreAlphaGameState>,
  trace: readonly HeadlessTraceEntry[],
): HeadlessRunSnapshot {
  return {
    actionCount: trace.length,
    turnNumber: state.turnNumber,
    finalStateHash: getStateHash(state),
    state: cloneSerializable(state),
    trace: cloneSerializable(trace),
  };
}

class HeadlessMatchRunnerImplementation implements HeadlessMatchRunner {
  #state: CurrentCoreAlphaGameState;
  readonly #trace: HeadlessTraceEntry[] = [];
  readonly #setup: MatchSetupInput;
  readonly #setupVerification: ReplayDefinitionV3["setupVerification"];
  readonly #context: EngineContext;
  readonly #options: NormalizedOptions;

  constructor(
    initialState: CurrentCoreAlphaGameState,
    setup: MatchSetupInput,
    setupVerification: ReplayDefinitionV3["setupVerification"],
    options: NormalizedOptions,
  ) {
    this.#state = initialState;
    this.#setup = cloneSerializable(setup);
    this.#setupVerification = cloneSerializable(setupVerification);
    this.#context = {
      rulesVersion: initialState.rulesVersion,
      cardDataVersion: initialState.cardDataVersion,
    };
    this.#options = options;
  }

  get state(): CurrentCoreAlphaGameState {
    return cloneSerializable(this.#state);
  }

  get trace(): readonly HeadlessTraceEntry[] {
    return cloneSerializable(this.#trace);
  }

  getLegalActions(): readonly GameAction[] {
    return cloneSerializable(getLegalActions(this.#state, this.#state.activePlayerId));
  }

  apply(action: GameAction): HeadlessStepResult {
    return this.#apply(action, "full");
  }

  #apply(action: GameAction, traceMode: HeadlessTraceMode): HeadlessStepResult {
    const incomingAction = cloneSerializable(action);
    const legalActions = getLegalActions(this.#state, this.#state.activePlayerId);
    if (!isActionInLegalSet(incomingAction, legalActions)) {
      return {
        ok: false,
        status: "illegal-action",
        failure: {
          code: "ACTION_NOT_IN_LEGAL_SET",
          message: "The action source returned an action outside authoritative getLegalActions.",
          details: {
            activePlayerId: this.#state.activePlayerId,
            phase: this.#state.phase,
            actionType: incomingAction.type,
          },
        },
      };
    }

    const stateHashBefore = traceMode === "full" ? getStateHash(this.#state) : null;
    let applied;
    try {
      applied = applyAction(this.#state, incomingAction, this.#context);
    } catch (error) {
      return {
        ok: false,
        status: "engine-failure",
        failure: {
          code: "ENGINE_APPLICATION_ERROR",
          message: error instanceof Error ? error.message : "Unknown engine application failure.",
          details: { actionType: incomingAction.type },
        },
      };
    }
    if (
      !applied.ok ||
      (applied.state.schemaVersion !== 3 &&
        applied.state.schemaVersion !== 4 &&
        applied.state.schemaVersion !== 5)
    ) {
      return {
        ok: false,
        status: "engine-failure",
        failure: applied.ok
          ? {
              code: "ENGINE_APPLICATION_ERROR",
              message: "A current headless match transitioned to an unsupported game-state schema.",
              details: { actualSchemaVersion: applied.state.schemaVersion },
            }
          : applied.failure,
      };
    }

    if (this.#options.invariantCheck === "every-step") {
      const failure = invariantFailure(applied.state);
      if (failure !== null) {
        return { ok: false, status: "invariant-failure", failure };
      }
    }

    const entry: HeadlessTraceEntry = {
      step: this.#trace.length,
      action: incomingAction,
      stateHashBefore,
      stateHashAfter: traceMode === "full" ? getStateHash(applied.state) : null,
      events: cloneSerializable(applied.events),
    };
    this.#state = applied.state;
    this.#trace.push(entry);
    return { ok: true, entry: cloneSerializable(entry) };
  }

  isFinished(): boolean {
    return this.#state.status.type === "finished";
  }

  exportReplay(): ReplayDefinitionV3 | ReplayDefinitionV4 | ReplayDefinitionV5 {
    const common = {
      rulesVersion: this.#context.rulesVersion,
      cardDataVersion: this.#context.cardDataVersion,
      setup: this.#setup,
      setupVerification: this.#setupVerification,
      actions: this.#trace.map((entry) => entry.action),
    };
    if (this.#state.schemaVersion === 4) {
      return cloneSerializable({ ...common, replayVersion: 4 }) as ReplayDefinitionV4;
    }
    if (this.#state.schemaVersion === 5) {
      return cloneSerializable({ ...common, replayVersion: 5 }) as ReplayDefinitionV5;
    }
    return cloneSerializable({ ...common, replayVersion: 3 }) as ReplayDefinitionV3;
  }

  run(source: HeadlessActionSource): HeadlessRunResult {
    while (true) {
      if (this.#state.status.type === "finished") {
        if (this.#options.invariantCheck === "final-only") {
          const failure = invariantFailure(this.#state);
          if (failure !== null)
            return { status: "invariant-failure", failure, ...snapshot(this.#state, this.#trace) };
        }
        return {
          status: "finished",
          winnerPlayerId: this.#state.status.winnerPlayerId,
          reason: this.#state.status.reason,
          ...snapshot(this.#state, this.#trace),
        };
      }
      if (this.#trace.length >= this.#options.maximumActions) {
        return { status: "action-limit-reached", ...snapshot(this.#state, this.#trace) };
      }
      if (this.#state.turnNumber > this.#options.maximumTurns) {
        return { status: "turn-limit-reached", ...snapshot(this.#state, this.#trace) };
      }

      const legalActions = getLegalActions(this.#state, this.#state.activePlayerId);
      let action: GameAction | null;
      try {
        action = source.nextAction({
          step: this.#trace.length,
          state: cloneSerializable(this.#state),
          legalActions: cloneSerializable(legalActions),
        });
      } catch (error) {
        return {
          status: "engine-failure",
          failure: {
            code: "ACTION_SOURCE_ERROR",
            message: error instanceof Error ? error.message : "Unknown action-source failure.",
            details: { step: this.#trace.length },
          },
          ...snapshot(this.#state, this.#trace),
        };
      }
      if (action === null) {
        return { status: "action-source-ended", ...snapshot(this.#state, this.#trace) };
      }

      const result = this.#apply(action, this.#options.traceMode);
      if (!result.ok) {
        return {
          status: result.status,
          failure: result.failure,
          ...snapshot(this.#state, this.#trace),
        };
      }
    }
  }

  runPlayerSources(sources: PlayerActionSources): HeadlessRunResult {
    while (true) {
      if (this.#state.status.type === "finished") {
        if (this.#options.invariantCheck === "final-only") {
          const failure = invariantFailure(this.#state);
          if (failure !== null)
            return { status: "invariant-failure", failure, ...snapshot(this.#state, this.#trace) };
        }
        return {
          status: "finished",
          winnerPlayerId: this.#state.status.winnerPlayerId,
          reason: this.#state.status.reason,
          ...snapshot(this.#state, this.#trace),
        };
      }
      if (this.#trace.length >= this.#options.maximumActions) {
        return { status: "action-limit-reached", ...snapshot(this.#state, this.#trace) };
      }
      if (this.#state.turnNumber > this.#options.maximumTurns) {
        return { status: "turn-limit-reached", ...snapshot(this.#state, this.#trace) };
      }

      const playerId = this.#state.activePlayerId;
      const source = sources[playerId];
      if (source.playerId !== playerId) {
        return {
          status: "engine-failure",
          failure: {
            code: "PLAYER_SOURCE_ID_MISMATCH",
            message: "A safe player source was registered under a different player ID.",
            details: { activePlayerId: playerId, sourcePlayerId: source.playerId },
          },
          ...snapshot(this.#state, this.#trace),
        };
      }

      const legalActions = getLegalActions(this.#state, playerId);
      if (legalActions.length === 0) {
        return {
          status: "engine-failure",
          failure: {
            code: "EMPTY_LEGAL_ACTION_SET",
            message:
              "An unfinished match produced no authoritative legal actions for its active player.",
            details: { step: this.#trace.length, playerId, phase: this.#state.phase },
          },
          ...snapshot(this.#state, this.#trace),
        };
      }
      let context: PlayerDecisionContext;
      try {
        context = createSafePlayerDecisionContext(
          this.#state,
          playerId,
          this.#trace.length,
          this.#options.playerObservationProjector,
        );
      } catch (error) {
        return {
          status: "engine-failure",
          failure: {
            code: "OBSERVATION_PROJECTION_ERROR",
            message:
              error instanceof Error
                ? error.message
                : "Unknown player observation projection failure.",
            details: { step: this.#trace.length, playerId },
          },
          ...snapshot(this.#state, this.#trace),
        };
      }

      let action: GameAction | null;
      try {
        action = source.nextAction(cloneSerializable(context));
      } catch (error) {
        const failure =
          error instanceof PlayerActionSourceFailure
            ? {
                code: error.code,
                message: error.message,
                details: { step: this.#trace.length, playerId, ...error.details },
              }
            : {
                code: "ACTION_SOURCE_ERROR" as const,
                message: error instanceof Error ? error.message : "Unknown action source failure.",
                details: { step: this.#trace.length, playerId },
              };
        return {
          status: "engine-failure",
          failure,
          ...snapshot(this.#state, this.#trace),
        };
      }
      if (action === null) {
        return { status: "action-source-ended", ...snapshot(this.#state, this.#trace) };
      }

      const result = this.#apply(action, this.#options.traceMode);
      if (!result.ok) {
        return {
          status: result.status,
          failure: result.failure,
          ...snapshot(this.#state, this.#trace),
        };
      }
    }
  }
}

/** Creates a DOM-free runner around a fresh, authoritative current Core Alpha match. */
export function createHeadlessMatchRunner(
  setup: MatchSetupInput,
  options?: HeadlessMatchRunnerOptions,
): HeadlessRunnerCreationResult {
  let normalizedOptions: NormalizedOptions;
  try {
    normalizedOptions = normalizeOptions(options);
  } catch (error) {
    return {
      ok: false,
      failure: {
        code: "INVARIANT_VIOLATION",
        message: error instanceof Error ? error.message : "Invalid runner option.",
        path: "options",
        details: {},
      },
    };
  }

  const created = createMatch(setup);
  if (!created.ok) return { ok: false, failure: created.failure };
  if (
    created.state.schemaVersion !== 3 &&
    created.state.schemaVersion !== 4 &&
    created.state.schemaVersion !== 5
  ) {
    return {
      ok: false,
      failure: {
        code: "UNSUPPORTED_GAME_STATE_SCHEMA",
        message: "The headless runner requires a schema-V3, schema-V4, or schema-V5 setup match.",
        path: "setup.catalog.schemaVersion",
        details: { actualStateSchemaVersion: created.state.schemaVersion },
      },
    };
  }
  const initialInvariantFailure = invariantFailure(created.state);
  if (initialInvariantFailure !== null) {
    return {
      ok: false,
      failure: {
        code: "INVARIANT_VIOLATION",
        message: initialInvariantFailure.message,
        path: null,
        details: initialInvariantFailure.details,
      },
    };
  }

  const replaySetup = cloneSerializable(setup) as MatchSetupInput;
  return {
    ok: true,
    runner: new HeadlessMatchRunnerImplementation(
      created.state,
      replaySetup,
      {
        gameplayCatalogHash: created.gameplayCatalogHash,
        initialRandomState: created.initialRandomState,
        finalRandomState: created.finalRandomState,
        initialStateHash: created.initialStateHash,
      },
      normalizedOptions,
    ),
  };
}
